// React imports
import React, { useEffect, useState } from 'react';

// API imports
import { doRule } from '../../api/rule';

// Component imports
import OtherEventsDataGrid from '../../component/DataGrid/OtherEventsDataGrid';
import UserActionsDataGrid from '../../component/DataGrid/UserActionsDataGrid';
import { SideMenu } from '../../component/Sidemenu/SideMenu';

// Modal imports
import { CreateRuleModal } from '../../component/modal/CreateRuleModal';
import { Do } from '../../component/modal/DoModal';
import { ConfirmDeleteRuleModal, EditRuleModal, RuleDescriptionModal, SetPointsModal } from 'src/component/modal';
import { SetTargetPoints } from '../../component/modal/UpdateTargetPoints';

// Context imports
import { useRuleContext } from '../../context/RuleStore';
import { useUserContext } from '../../context/UserStore';

// Material-UI imports
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, Button, Card, CardContent, CardHeader, InputAdornment, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styles
import './HomeView.css';

// Styled components for animations
const FloatingRuleCard = styled('div')(({ theme, isPositive }) => ({
  display: 'flex',
  alignItems: 'center',
  animation: 'float 6s ease-in-out infinite',
  '&:nth-of-type(2n)': {
    animationDelay: '1s',
  },
  '&:nth-of-type(3n)': {
    animationDelay: '2s',
  },
  '&:nth-of-type(4n)': {
    animationDelay: '3s',
  },
  
  // Style the individual rule boxes with conditional gradients
  '& .MuiBox-root': {
    // Green to cyan gradient for positive rules, red to orange for negative rules
    background: isPositive 
      ? 'linear-gradient(135deg, #10b981 0%, #00bcd4 50%, #006064 100%)'
      : 'linear-gradient(135deg, #ff4757 0%, #ff6b35 50%, #ff9500 100%)',
    color: 'white',
    fontWeight: 'bold',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    transition: 'all 0.3s ease',
    
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: isPositive 
        ? '0 8px 25px rgba(16, 185, 129, 0.4)'
        : '0 8px 25px rgba(255, 71, 87, 0.4)',
    }
  },
  
  '@keyframes float': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    }
  }
}));

// Fast floating animation for user info elements
const FastFloatingElement = styled('div')(({ theme }) => ({
  animation: 'fastFloat 3s ease-in-out infinite',
  
  '&:nth-of-type(2n)': {
    animationDelay: '0.5s',
  },
  '&:nth-of-type(3n)': {
    animationDelay: '1s',
  },
  '&:nth-of-type(4n)': {
    animationDelay: '1.5s',
  },
  
  '@keyframes fastFloat': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-20px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    }
  }
}));

const GradientRulesCard = styled(Card)(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  flexWrap: 'wrap',
  // Black and white vertical gradient
  background: 'linear-gradient(to bottom, #4a4a4a 0%, #808080 25%, #ffffff 50%, #808080 75%, #4a4a4a 100%)',
  backgroundSize: '100% 400%',
  animation: 'blackWhiteVertical 4s ease infinite',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  
  '& .MuiCardContent-root': {
    background: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '8px',
    backdropFilter: 'blur(10px)',
  },
  
  // Make text white for better contrast
  '& .MuiTypography-root': {
    color: 'white',
  },
  
  // Style the search field for the black background
  '& .MuiTextField-root': {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
    '& .MuiInput-root': {
      color: 'white',
      '&:before': {
        borderBottomColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&:hover:before': {
        borderBottomColor: 'white',
      },
      '&:after': {
        borderBottomColor: 'white',
      },
    },
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
  },
  
  '@keyframes blackWhiteVertical': {
    '0%': {
      backgroundPosition: '0% 0%',
    },
    '50%': {
      backgroundPosition: '0% 100%',
    },
    '100%': {
      backgroundPosition: '0% 0%',
    }
  }
}));

// Rainbow gradient description button with 5-second animation
const RainbowDescriptionButton = styled(Button)(({ theme }) => ({
  border: '20px ridge black',
  background: 'linear-gradient(-45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7, #fab1a0, #fd79a8, #a29bfe)',
  backgroundSize: '400% 400%',
  animation: 'rainbowDescription 5s ease infinite',
  color: 'white',
  fontWeight: 'bold',
  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
  
  '@keyframes rainbowDescription': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    }
  },
  
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
  }
}));

// Floating Do button with pastel rainbow gradient
const FloatingDoButton = styled(Button)(({ theme }) => ({
  border: '20px ridge black',
  background: 'linear-gradient(-45deg, #ffcccb, #add8e6, #90ee90, #f0e68c, #dda0dd, #ffd1dc, #98fb98, #b19cd9)',
  backgroundSize: '400% 400%',
  animation: 'floatPastelRainbow 4s ease infinite, pastelRainbow 3s ease infinite',
  color: '#333',
  fontWeight: 'bold',
  textShadow: '1px 1px 2px rgba(255,255,255,0.5)',
  
  '@keyframes floatPastelRainbow': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-15px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    }
  },
  
  '@keyframes pastelRainbow': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    }
  },
  
  '&:hover': {
    transform: 'scale(1.05) translateY(-5px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
  }
}));

// Notification overlay styled component
const NotificationOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  
  '&.fade-out': {
    animation: 'fadeOut 1s ease-out forwards',
  },
  
  '@keyframes fadeOut': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
      visibility: 'hidden',
    }
  }
}));

// Notification message with light speed animations
const NotificationMessage = styled('h1')(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  margin: 0,
  animation: 'lightSpeedInRight 1.5s ease-out, lightSpeedOutRight 1.5s ease-in 2s forwards',
  
  '@keyframes lightSpeedInRight': {
    '0%': {
      opacity: 0,
      transform: 'translateX(100vw) skewX(-30deg)',
    },
    '60%': {
      opacity: 1,
      transform: 'translateX(0px) skewX(20deg)',
    },
    '80%': {
      transform: 'translateX(0px) skewX(-5deg)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0px) skewX(0deg)',
    }
  },
  
  '@keyframes lightSpeedOutRight': {
    '0%': {
      opacity: 1,
      transform: 'translateX(0px) skewX(0deg)',
    },
    '100%': {
      opacity: 0,
      transform: 'translateX(100vw) skewX(30deg)',
    }
  },
  
  // Responsive font sizes
  '@media (max-width: 768px)': {
    fontSize: '2.5rem',
  },
  '@media (max-width: 480px)': {
    fontSize: '1.8rem',
  }
}));

// Delete notification overlay with flip animations
const DeleteNotificationOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  
  '&.fade-out': {
    animation: 'fadeOut 1s ease-out forwards',
  },
  
  '@keyframes fadeOut': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
      visibility: 'hidden',
    }
  }
}));

// Delete notification message with flip animations
const DeleteNotificationMessage = styled('h1')(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  margin: 0,
  animation: 'flipInX 1.5s ease-out, flipOutX 1.5s ease-in 2s forwards',
  
  '@keyframes flipInX': {
    '0%': {
      opacity: 0,
      transform: 'perspective(400px) rotateX(90deg)',
    },
    '40%': {
      transform: 'perspective(400px) rotateX(-20deg)',
    },
    '60%': {
      transform: 'perspective(400px) rotateX(10deg)',
    },
    '80%': {
      transform: 'perspective(400px) rotateX(-5deg)',
    },
    '100%': {
      opacity: 1,
      transform: 'perspective(400px) rotateX(0deg)',
    }
  },
  
  '@keyframes flipOutX': {
    '0%': {
      opacity: 1,
      transform: 'perspective(400px) rotateX(0deg)',
    },
    '30%': {
      transform: 'perspective(400px) rotateX(-20deg)',
    },
    '100%': {
      opacity: 0,
      transform: 'perspective(400px) rotateX(90deg)',
    }
  },
  
  // Responsive font sizes
  '@media (max-width: 768px)': {
    fontSize: '2.5rem',
  },
  '@media (max-width: 480px)': {
    fontSize: '2rem',
  }
}));

// Goal/Task notification overlay with flipY animations
const GoalTaskNotificationOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  
  '&.fade-out': {
    animation: 'fadeOut 1s ease-out forwards',
  },
  
  '@keyframes fadeOut': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
      visibility: 'hidden',
    }
  }
}));

// Goal/Task notification message with flipY animations
const GoalTaskNotificationMessage = styled('h1')(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  margin: 0,
  animation: 'flipInY 1.5s ease-out, flipOutY 1.5s ease-in 2s forwards',
  
  '@keyframes flipInY': {
    '0%': {
      opacity: 0,
      transform: 'perspective(400px) rotateY(90deg)',
    },
    '40%': {
      transform: 'perspective(400px) rotateY(-20deg)',
    },
    '60%': {
      transform: 'perspective(400px) rotateY(10deg)',
    },
    '80%': {
      transform: 'perspective(400px) rotateY(-5deg)',
    },
    '100%': {
      opacity: 1,
      transform: 'perspective(400px) rotateY(0deg)',
    }
  },
  
  '@keyframes flipOutY': {
    '0%': {
      opacity: 1,
      transform: 'perspective(400px) rotateY(0deg)',
    },
    '30%': {
      transform: 'perspective(400px) rotateY(-20deg)',
    },
    '100%': {
      opacity: 0,
      transform: 'perspective(400px) rotateY(90deg)',
    }
  },
  
  // Responsive font sizes
  '@media (max-width: 768px)': {
    fontSize: '2.5rem',
  },
  '@media (max-width: 480px)': {
    fontSize: '2rem',
  }
}));

// Animated Create Rule Button with hover effects
const AnimatedCreateButton = styled('button')(({ theme }) => ({
  width: '90px',
  height: '45px',
  background: 'linear-gradient(90deg, rgba(255,0,0,1) 3%, rgba(255,144,0,1) 12%, rgba(238,255,0,1) 24%, rgba(70,255,0,1) 36%, rgba(0,237,255,1) 48%, rgba(0,37,255,1) 60%, rgba(137,0,255,1) 72%, rgba(255,0,132,1) 84%, rgba(255,0,0,1) 97%, rgba(255,0,250,1) 100%)',
  borderRadius: '50%',
  color: 'azure',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 'bold',
  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  transform: 'translateY(0px)',
  boxShadow: '0 8px 16px rgba(0,0,0,0.6), 0 4px 8px rgba(0,0,0,0.4)',
  position: 'relative',
  
  // Default state - static, no floating
  animation: 'none',
  
  // Hover state - move up and start floating with increased shadow
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 25px 50px rgba(0,0,0,0.8), 0 15px 30px rgba(0,0,0,0.6)',
    animation: 'hoverFloat 2s ease-in-out infinite',
  },
  
  // Burst animation when mouse leaves
  '&.burst': {
    animation: 'crashBurst 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards',
  },
  
  '@keyframes hoverFloat': {
    '0%': {
      transform: 'translateY(-10px)',
    },
    '50%': {
      transform: 'translateY(-20px)',
    },
    '100%': {
      transform: 'translateY(-10px)',
    }
  },
  
  '@keyframes crashBurst': {
    '0%': {
      transform: 'translateY(-10px)',
      boxShadow: '0 25px 50px rgba(0,0,0,0.8), 0 15px 30px rgba(0,0,0,0.6)',
    },
    '30%': {
      transform: 'translateY(15px) scale(1.2)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    '50%': {
      transform: 'translateY(10px) scale(1.15)',
      boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
    },
    '70%': {
      transform: 'translateY(-5px) scale(1.05)',
      boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
    },
    '85%': {
      transform: 'translateY(2px) scale(1.02)',
      boxShadow: '0 7px 14px rgba(0,0,0,0.5)',
    },
    '100%': {
      transform: 'translateY(0px) scale(1)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.6), 0 4px 8px rgba(0,0,0,0.4)',
    }
  },
  
  // Active state for click feedback
  '&:active': {
    transform: 'translateY(-8px) scale(0.95)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.7), 0 8px 16px rgba(0,0,0,0.5)',
  }
}));

/**
 * HomeView Component
 * Main dashboard component that displays user information, rules, and data grids
 */
export const HomeView = () => {
  // Context hooks for state management
  const { state: userState, action: userActions } = useUserContext();
  const { state: ruleState, action: ruleActions } = useRuleContext();
  
  // Local state for search functionality and filtered rules
  const [search, setSearch] = useState('');
  const [rules, setRules] = useState(ruleState?.rules);
  const [ruleFilter, setRuleFilter] = useState('all'); // 'all', 'positive', 'negative'
  const [isBursting, setIsBursting] = useState(false);
  
  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [fadeOutNotification, setFadeOutNotification] = useState(false);
  
  // Delete notification state
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const [deleteNotificationMessage, setDeleteNotificationMessage] = useState('');
  const [fadeOutDeleteNotification, setFadeOutDeleteNotification] = useState(false);
  
  // Goal/Task notification state
  const [goalTaskNotificationIsVisible, setGoalTaskNotificationIsVisible] = useState(false);
  const [goalTaskNotificationMessage, setGoalTaskNotificationMessage] = useState('');
  const [fadeOutGoalTaskNotification, setFadeOutGoalTaskNotification] = useState(false);
  
  // UI Component - Bullet point decorator (currently unused)
  const Bull = () => <div id="bull" />;

  // Handle hover events for Create Rule button
  const handleMouseEnter = () => {
    setIsBursting(false);
  };

  const handleMouseLeave = () => {
    setIsBursting(true);
    // Reset burst animation after it completes
    setTimeout(() => setIsBursting(false), 800);
  };

  // Show notification function
  const showRuleNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setFadeOutNotification(false);

    // Start fade out after 3 seconds (after light speed animations)
    const fadeTimer = setTimeout(() => {
      setFadeOutNotification(true);
    }, 3000);

    // Hide notification completely after fade out
    const hideTimer = setTimeout(() => {
      setShowNotification(false);
      setFadeOutNotification(false);
    }, 4000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  };

  // Show delete notification function
  const showDeleteRuleNotification = (message) => {
    setDeleteNotificationMessage(message);
    setShowDeleteNotification(true);
    setFadeOutDeleteNotification(false);

    // Start fade out after 3 seconds (after flip animations)
    const fadeTimer = setTimeout(() => {
      setFadeOutDeleteNotification(true);
    }, 3000);

    // Hide notification completely after fade out
    const hideTimer = setTimeout(() => {
      setShowDeleteNotification(false);
      setFadeOutDeleteNotification(false);
    }, 4000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  };

  // Show goal/task notification function
  const goalTaskNotification = (message) => {
    setGoalTaskNotificationMessage(message);
    setGoalTaskNotificationIsVisible(true);
    setFadeOutGoalTaskNotification(false);

    // Start fade out after 3 seconds (after flip animations)
    const fadeTimer = setTimeout(() => {
      setFadeOutGoalTaskNotification(true);
    }, 3000);

    // Hide notification completely after fade out
    const hideTimer = setTimeout(() => {
      setGoalTaskNotificationIsVisible(false);
      setFadeOutGoalTaskNotification(false);
    }, 1000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  };

  // Effect hooks for data initialization and search functionality
  
  // Initialize user data on component mount
  useEffect(() => {
    userActions.getUserDataAsyncAction();
  }, []);

  // Initialize rules data on component mount
  useEffect(() => {
    ruleActions.getRulesAsyncAction();
  }, []);
  
  // Filter rules based on search input and positive/negative filter
  useEffect(() => {
    let filteredRules = ruleState.rules || [];
    
    // Apply search filter
    if (search.trim() !== '') {
      filteredRules = filteredRules.filter((item) => 
        item.name.toLowerCase().includes(search.trim().toLowerCase())
      );
    }
    
    // Apply positive/negative filter
    if (ruleFilter === 'positive') {
      filteredRules = filteredRules.filter((item) => item.points > 0);
    } else if (ruleFilter === 'negative') {
      filteredRules = filteredRules.filter((item) => item.points < 0);
    }
    
    setRules(filteredRules);
  }, [search, ruleFilter, ruleState.rules]);
  
  // Event handlers
  
  /**
   * Handle search input changes
   * @param {Object} event - Input change event
   */
  const handleChange = (event) => {
    setSearch(event.target.value);
  };
  
  /**
   * Handle rule filter changes
   * @param {string} filter - Filter type ('all', 'positive', 'negative')
   */
  const handleFilterChange = (filter) => {
    setRuleFilter(filter);
  };
  
  return (
    <>
      {/* Notification Overlays */}
      {showNotification && (
        <NotificationOverlay className={fadeOutNotification ? 'fade-out' : ''}>
          <NotificationMessage>{notificationMessage}</NotificationMessage>
        </NotificationOverlay>
      )}
      {showDeleteNotification && (
        <DeleteNotificationOverlay className={fadeOutDeleteNotification ? 'fade-out' : ''}>
          <DeleteNotificationMessage>{deleteNotificationMessage}</DeleteNotificationMessage>
        </DeleteNotificationOverlay>
      )}
      {goalTaskNotificationIsVisible && (
        <GoalTaskNotificationOverlay className={fadeOutGoalTaskNotification ? 'fade-out' : ''}>
          <GoalTaskNotificationMessage>{goalTaskNotificationMessage}</GoalTaskNotificationMessage>
        </GoalTaskNotificationOverlay>
      )}

      {/* TODO: Consider using these images in the future */}
      {/* <img src="/image/hugs-mobile.svg"/>
      <img src='/image/favicon2.png'/> */}
      
      {/* Main Action Button - Create Rule */}
      <div style={{ textAlign: 'center' }}>
        <AnimatedCreateButton
          className={isBursting ? 'burst' : ''}
          onClick={ruleActions.showEditRuleModalAction}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          Create Rule
        </AnimatedCreateButton>
      </div>
      
      <br />

      {/* User Information Section */}
      <FastFloatingElement style={{ animationDelay: '0.3s' }}>
        <div style={{ maxWidth: '100px', margin: 'auto', display: 'flex', flexDirection: 'row' }}>
          &nbsp;<pre className="text">Username:</pre>&nbsp;<pre className='space text'>{userState.username}</pre><br />
        </div>
      </FastFloatingElement>
      
      <FastFloatingElement style={{ animationDelay: '0.6s' }}>
        <div style={{ maxWidth: '100px', margin: 'auto', display: 'flex', flexDirection: 'row' }}>
          &nbsp;<pre className="text">Target Points:</pre>&nbsp;<pre className='space text'>{userState.targetPoints}</pre>
        </div>
      </FastFloatingElement>
      
      {/* Current Points Display with Highlighted Border */}
      <FastFloatingElement style={{ animationDelay: '0.9s' }}>
        <div style={{ maxWidth: '100px', margin: 'auto', display: 'flex', flexDirection: 'row' }}>
          &nbsp;<pre className="text">Points:</pre>{' '}
          <div 
            style={{
              margin: 'auto', 
              border: '20px ridge azure', 
              borderRadius: '40%', 
              maxWidth: '60px', 
              padding: '10px'
            }} 
            className="space text"
          >
            <div className='text'>{userState.points}</div>
          </div>
        </div>
      </FastFloatingElement>
      
      <br />
      <br />
      
      {/* User Action Modals */}
      <FastFloatingElement style={{ animationDelay: '1.2s', textAlign: 'center' }}>
        <SetPointsModal c={userActions.getUserDataAsync} />
      </FastFloatingElement>
      
      <br />
      
      <FastFloatingElement style={{ animationDelay: '1.5s', textAlign: 'center' }}>
        <SetTargetPoints />
      </FastFloatingElement>
      
      {/* Rules Section */}
      <GradientRulesCard sx={{ margin: 2 }}>
        <CardContent>
          {/* Section Header */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
              Rules
            </Typography>
          </div>
          
          {/* Search Input Field */}
          <div style={{ margin: 'auto', display: 'flex', justifyContent: 'center', width: '100%', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              label="Search"
              id="standard-basic"
              variant="standard"
              onChange={handleChange}
              value={search}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ marginBottom: 2 }}
            />
            
            {/* Rule Filter Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <Button
                variant={ruleFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange('all')}
                sx={{ 
                  color: ruleFilter === 'all' ? 'black' : 'white',
                  backgroundColor: ruleFilter === 'all' ? 'white' : 'transparent',
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                All Rules
              </Button>
              <Button
                variant={ruleFilter === 'positive' ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange('positive')}
                sx={{ 
                  color: ruleFilter === 'positive' ? 'black' : 'white',
                  backgroundColor: ruleFilter === 'positive' ? '#10b981' : 'transparent',
                  borderColor: '#10b981',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  }
                }}
              >
                Positive (+)
              </Button>
              <Button
                variant={ruleFilter === 'negative' ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange('negative')}
                sx={{ 
                  color: ruleFilter === 'negative' ? 'white' : 'white',
                  backgroundColor: ruleFilter === 'negative' ? '#ff4757' : 'transparent',
                  borderColor: '#ff4757',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 71, 87, 0.1)',
                  }
                }}
              >
                Negative (-)
              </Button>
            </div>
          </div>
          
          <Typography variant="h5" component="div"></Typography>
          
          {/* Rules Grid Display */}
          <div style={{ margin: 'auto', display: 'flex', flexWrap: 'wrap', width: '2000px' }} sx={{ margin: 2 }}>
            {rules?.map((rule, index) => {
              const isPositive = rule.points > 0;
              return (
                <FloatingRuleCard key={rule.id} style={{ animationDelay: `${index * 0.5}s` }} isPositive={isPositive}>
                  <Box sx={{ boxShadow: 5 }} style={{ display: 'flex', padding: '10px', alignItems: 'center', margin: '10px', borderRadius: '50px' }}>
                    
                    {/* Rule Information Display */}
                    <Box 
                      key={`${rule.name}`} 
                      sx={{ boxShadow: 5 }} 
                      style={{ display: 'flex', padding: '10px', alignItems: 'center', margin: '10px', borderRadius: '50px' }}
                    >
                      {rule.name}:&nbsp;&nbsp;{rule.points}
                    </Box>
                    
                    &nbsp;&nbsp;&nbsp;
                    
                    {/* Rule Action Buttons */}
                    <RainbowDescriptionButton
                      onClick={() => {
                        ruleActions.selectRule(rule);
                        ruleActions.showRuleDescriptionModalAction();
                      }}
                    >
                      Description
                    </RainbowDescriptionButton>
                    
                    &nbsp;&nbsp;&nbsp;
                    
                    {/* Rule Action Components */}
                    <Do id={rule.id} overwritePoints={rule.points} />
                    <ConfirmDeleteRuleModal 
                      id={rule.id} 
                      name={rule.name} 
                      onRuleDeleted={() => showDeleteRuleNotification('Rule has been deleted!')}
                    />
                    
                    &nbsp;&nbsp;&nbsp;
                    
                    <EditRuleModal 
                      name={rule.name} 
                      points={rule.points} 
                      description={rule.description} 
                      id={rule.id} 
                      onRuleEdited={() => showRuleNotification('Rule has been edited!')}
                    />
                  </Box>
                </FloatingRuleCard>
              );
            })}
          </div>
          
          {/* Debug information (commented out) */}
          {/* <pre>{JSON.stringify(isCheckedRules, null, 4)}</pre> */}
        </CardContent>
      </GradientRulesCard>
      
      {/* History Section */}
      <Card variant="outlined" sx={{ minWidth: 275, margin: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
            History
          </Typography>
          <Typography variant="h5" component="div"></Typography>
        </CardContent>
      </Card>
      
      {/* Data Grids Section */}
      <UserActionsDataGrid />
      <OtherEventsDataGrid />
      
      {/* Modal Components */}
      <CreateRuleModal onRuleCreated={() => showRuleNotification('Rule created!')} />
      <RuleDescriptionModal />
    </>
  );
};

// Note: The commented code below appears to be leftover debug/development content
// Consider removing if no longer needed